import { useState } from "react";
import api from "@/lib/api";
import { formatApiError, limitWords, countWords } from "@/lib/format";
import { toast } from "sonner";
import { Gate, ready, Panel, useLoad, stamp, Chip, Filters, Modal, fieldStyle, Label } from "@/components/console/ui";
import { openFile } from "@/components/undertake/ReviewTab";

function DeclineModal({ r, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!reason.trim()) return;
    setBusy(true);
    try { await api.post(`/console/xp-requests/${r.id}/decline`, { reason }); toast.success("declined · the user sees your reason"); onDone(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); setBusy(false); }
  };
  return (
    <Modal title={`decline · ${r.title}`} onClose={onClose} testId="xp-decline-modal">
      <Label>reason (shown to the user) · {countWords(reason)}/33</Label>
      <textarea data-testid="xp-decline-reason" value={reason} onChange={(e) => setReason(limitWords(e.target.value, reason, 33))} rows={3} className="w-full p-3 text-sm resize-none" style={fieldStyle} />
      <button onClick={submit} disabled={busy || !reason.trim()} data-testid="xp-decline-confirm" className="btn w-full py-3 mt-4 text-xs tracking-widest">{busy ? "…" : "confirm decline"}</button>
    </Modal>
  );
}

export function XpQueue() {
  const [status, setStatus] = useState("pending");
  const [rows, reload] = useLoad(`/console/xp-requests?status=${status}`, 10000);
  const [declining, setDeclining] = useState(null);
  const [busy, setBusy] = useState(null);
  const accept = async (r) => {
    if (busy) return;
    setBusy(r.id);
    try { await api.post(`/console/xp-requests/${r.id}/accept`); toast.success(`verified · ${r.title}`); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(null); reload(); }
  };
  return (
    <Panel title="natural xp verification · oldest first" testId="console-xp"
      right={<Filters testId="xp-filter" value={status} onChange={setStatus} options={[["pending", "pending"], ["accepted", "accepted"], ["declined", "declined"], ["", "all"]]} />}>
      {!ready(rows) ? <Gate data={rows} testId="xp" /> : !rows.length ? <p className="text-xs opacity-60 py-6" data-testid="xp-empty">{status === "pending" ? "queue empty · no xp proof waiting" : "nothing here"}</p> : (
        <ul className="flex flex-col gap-4">
          {rows.map((r) => (
            <li key={r.id} className="p-4" style={{ border: "1px solid var(--border)", background: "var(--bg-primary)" }} data-testid={`xp-item-${r.id}`}>
              <div className="flex flex-wrap items-center gap-3 mb-3">
                <Chip on={r.status === "pending"}>{r.status}</Chip>
                <span className="text-xs">by <span style={{ color: "var(--accent)" }}>{r.user_name}</span> · entry {r.index + 1}</span>
                <span className="text-xs opacity-60 ml-auto">sent {stamp(r.sent_at)}</span>
              </div>
              <p className="font-head text-lg mb-1" data-testid={`xp-item-title-${r.id}`}>{r.title}</p>
              {r.description && <p className="long-text-sm text-sm leading-relaxed mb-3 opacity-80">{r.description}</p>}
              <Label>proof files</Label>
              <div className="flex flex-wrap gap-2 mb-4">
                {r.files.map((f) => <button key={f.id} onClick={() => openFile(r.id, f, "xp-requests")} data-testid={`xp-file-${f.id}`} className="btn px-3 py-1 text-[10px] tracking-widest">↓ {f.name || "file"}</button>)}
              </div>
              {r.status === "declined" && <p className="text-xs" style={{ color: "var(--accent)" }}>reason · {r.reason}</p>}
              {r.status === "pending" && (
                <div className="grid grid-cols-2 gap-3">
                  <button onClick={() => setDeclining(r)} data-testid={`xp-decline-${r.id}`} className="btn py-3 text-xs tracking-widest">decline</button>
                  <button onClick={() => accept(r)} disabled={busy === r.id} data-testid={`xp-accept-${r.id}`} className="btn py-3 text-xs tracking-widest">{busy === r.id ? "…" : "accept & verify"}</button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
      {declining && <DeclineModal r={declining} onClose={() => setDeclining(null)} onDone={() => { setDeclining(null); reload(); }} />}
    </Panel>
  );
}
